import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  formatMoney,
  QboAccount,
  QboActivityRow,
  QboItem,
  QboStatus,
  quickbooks,
  QboTaxOverview,
} from "../api/client";

const EXPENSE_TYPES = ["Expense", "Other Expense", "Cost of Goods Sold"];
const PAYMENT_TYPES = ["Bank", "Credit Card"];

function paymentTypeFor(account?: QboAccount): string {
  return account?.account_type === "Credit Card" ? "CreditCard" : "Cash";
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export default function QuickBooks() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState<QboStatus | null>(null);
  const [accounts, setAccounts] = useState<QboAccount[]>([]);
  const [items, setItems] = useState<QboItem[]>([]);
  const [activity, setActivity] = useState<QboActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const s = await quickbooks.status();
      setStatus(s);
      if (s.connected) {
        const [accts, acts, its] = await Promise.all([
          quickbooks.accounts().catch(() => [] as QboAccount[]),
          quickbooks.activity().catch(() => [] as QboActivityRow[]),
          quickbooks.items().catch(() => [] as QboItem[]),
        ]);
        setAccounts(accts);
        setActivity(acts);
        setItems(its);
      } else {
        setAccounts([]);
        setActivity([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load QuickBooks status");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returning from the Intuit OAuth redirect (?qbo=connected).
  useEffect(() => {
    const result = params.get("qbo");
    if (result === "connected") {
      setToast("QuickBooks Online connected.");
      setParams({}, { replace: true });
      load();
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
    if (result === "error") {
      setError(`QuickBooks connection failed: ${params.get("error") ?? "unknown error"}`);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expenseAccounts = useMemo(
    () => accounts.filter((a) => EXPENSE_TYPES.includes(a.account_type || "")),
    [accounts],
  );
  const paymentAccounts = useMemo(
    () => accounts.filter((a) => PAYMENT_TYPES.includes(a.account_type || "")),
    [accounts],
  );
  const apAccounts = useMemo(
    () => accounts.filter((a) => (a.account_type || "") === "Accounts Payable"),
    [accounts],
  );
  const depositAccounts = useMemo(
    () => accounts.filter((a) => ["Bank", "Other Current Asset"].includes(a.account_type || "")),
    [accounts],
  );

  async function handleConnect() {
    setBusy(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/app/quickbooks`;
      const res = await quickbooks.connectStart(returnUrl);
      window.location.href = res.authorize_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start QuickBooks connect");
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Disconnect QuickBooks Online? Receipts will stop syncing.")) return;
    setBusy(true);
    try {
      await quickbooks.disconnect();
      setToast("QuickBooks disconnected.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Disconnect failed");
    } finally {
      setBusy(false);
    }
  }

  async function patch(body: Parameters<typeof quickbooks.update>[0]) {
    setBusy(true);
    setError(null);
    try {
      const s = await quickbooks.update(body);
      setStatus(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  function onPickExpense(id: string) {
    const acct = expenseAccounts.find((a) => a.id === id);
    patch({ default_expense_account_id: id, default_expense_account_name: acct?.name });
  }

  function onPickPayment(id: string) {
    const acct = paymentAccounts.find((a) => a.id === id);
    patch({
      default_payment_account_id: id,
      default_payment_account_name: acct?.name,
      default_payment_type: paymentTypeFor(acct),
    });
  }

  function onPickAp(id: string) {
    const acct = apAccounts.find((a) => a.id === id);
    patch({ default_ap_account_id: id, default_ap_account_name: acct?.name });
  }

  function onPickIncomeItem(id: string) {
    const item = items.find((i) => i.id === id);
    patch({ default_income_item_id: id, default_income_item_name: item?.name });
  }

  function onPickDeposit(id: string) {
    const acct = depositAccounts.find((a) => a.id === id);
    patch({ default_deposit_account_id: id, default_deposit_account_name: acct?.name });
  }

  async function handleSyncAll() {
    setBusy(true);
    setError(null);
    try {
      const res = await quickbooks.syncBulk();
      setToast(
        `Synced ${res.synced} · skipped ${res.skipped} · ${res.errors} error${res.errors === 1 ? "" : "s"}.`,
      );
      const acts = await quickbooks.activity().catch(() => [] as QboActivityRow[]);
      setActivity(acts);
      const s = await quickbooks.status().catch(() => status);
      if (s) setStatus(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bulk sync failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="page-empty">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>QuickBooks Online</h1>
      </div>

      {toast && <div className="page-toast">{toast}</div>}
      {error && <div className="page-error">{error}</div>}
      {status?.mode === "fake" && (
        <div className="page-toast" style={{ marginBottom: 12 }}>
          Demo mode: this server's QuickBooks connector is simulated (RECEIPTS_QBO_MODE=fake). Nothing is sent to Intuit.
        </div>
      )}

      {!status?.connected ? (
        <div className="detail-card">
          <h3 style={{ marginTop: 0 }}>Connect QuickBooks Online</h3>
          <p style={{ color: "var(--text-soft)", maxWidth: 560 }}>
            Connect your QuickBooks Online company to push receipts straight into
            your books. Paid receipts post as <strong>Purchases</strong>, unpaid
            invoices as <strong>Bills</strong>, and income as{" "}
            <strong>Sales Receipts</strong>, with the original PDF or image
            attached. Vendors and customers are matched or created automatically.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConnect}
            disabled={busy}
          >
            {busy ? "Connecting…" : "Connect QuickBooks"}
          </button>
        </div>
      ) : (
        <>
          <div className="detail-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  Connected{status.company_name ? ` · ${status.company_name}` : ""}
                </h3>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
                  Company file ····{status.realm_id_last4 || "????"} · Last sync{" "}
                  {formatDateTime(status.last_sync_at)} · {status.pending_count ?? 0} receipt
                  {status.pending_count === 1 ? "" : "s"} not yet sent
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSyncAll}
                  disabled={busy || !status.sync_enabled}
                  title={
                    status.sync_enabled
                      ? "Sync every eligible receipt that isn't in QuickBooks yet"
                      : "Enable syncing first"
                  }
                >
                  {busy ? "Working…" : "Send all pending receipts"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDisconnect}
                  disabled={busy}
                >
                  Disconnect
                </button>
              </div>
            </div>

            <label
              style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}
            >
              <input
                type="checkbox"
                checked={!!status.sync_enabled}
                onChange={(e) => patch({ sync_enabled: e.target.checked })}
                disabled={busy}
              />
              Syncing enabled
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                checked={!!status.auto_sync}
                onChange={(e) => patch({ auto_sync: e.target.checked })}
                disabled={busy || !status.sync_enabled}
              />
              Automatically send receipts to QuickBooks once you mark them reviewed
            </label>
          </div>

          <div className="detail-card">
            <h3 style={{ marginTop: 0 }}>Posting accounts</h3>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
              Where synced receipts land in your chart of accounts. We picked
              sensible defaults when you connected — change them anytime.
            </p>

            <div className="detail-row">
              <span className="label">Expense account</span>
              <span className="value">
                <select
                  value={status.default_expense_account_id || ""}
                  onChange={(e) => onPickExpense(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select…</option>
                  {expenseAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <div className="detail-row">
              <span className="label">Payment account (for paid receipts)</span>
              <span className="value">
                <select
                  value={status.default_payment_account_id || ""}
                  onChange={(e) => onPickPayment(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select…</option>
                  {paymentAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.account_type})
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <div className="detail-row">
              <span className="label">Accounts Payable (for bills)</span>
              <span className="value">
                <select
                  value={status.default_ap_account_id || ""}
                  onChange={(e) => onPickAp(e.target.value)}
                  disabled={busy}
                >
                  <option value="">QuickBooks default</option>
                  {apAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <div className="detail-row">
              <span className="label">Income item (for income)</span>
              <span className="value">
                <select
                  value={status.default_income_item_id || ""}
                  onChange={(e) => onPickIncomeItem(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Select…</option>
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>

            <div className="detail-row">
              <span className="label">Deposit to (for income)</span>
              <span className="value">
                <select
                  value={status.default_deposit_account_id || ""}
                  onChange={(e) => onPickDeposit(e.target.value)}
                  disabled={busy}
                >
                  <option value="">QuickBooks default (Undeposited Funds)</option>
                  {depositAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>

          </div>

          <TaxMappings />

          <div className="detail-card">
            <h3 style={{ marginTop: 0 }}>Recent activity</h3>
            {activity.length === 0 ? (
              <div className="page-empty">No sync activity yet.</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td>
                        {row.kind}
                        {row.entity_type ? ` · ${row.entity_type}` : ""}
                      </td>
                      <td>{row.status}</td>
                      <td style={{ maxWidth: 360 }}>{row.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Set & Ledger tax codes and the QuickBooks code each posts with. Mapped once,
 *  so new receipts sync without being revisited. */
function TaxMappings() {
  const [data, setData] = useState<QboTaxOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    quickbooks
      .taxMappings()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load tax codes"));
  }, []);

  async function run(key: string, action: () => Promise<QboTaxOverview>) {
    setBusy(key);
    setError(null);
    try {
      setData(await action());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  }

  function create(key: string, label: string) {
    if (!window.confirm(`Create a tax code for “${label}” in your QuickBooks company?\n\nIt's filed with the ` +
      "Canada Revenue Agency on purchases and sales, so its credits appear on your GST/HST return.")) return;
    run(key, () => quickbooks.createTaxMapping(key));
  }

  if (error && !data) return <div className="page-error">{error}</div>;
  if (!data || !data.uses_tax_codes) return null;
  const rateLabel = (rate: number | null) => (rate == null ? "" : ` (${rate}%)`);
  const shown = showAll ? data.receipts : data.receipts.slice(0, 6);

  return (
    <div className="detail-card tax-map">
      <h3 style={{ marginTop: 0 }}>Tax codes</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Every receipt, bill and income gets a Set &amp; Ledger tax code from the tax it shows (you can change it on the
        receipt). Map each one to a QuickBooks tax code once; new receipts then sync without being revisited.
      </p>
      {error && <div className="page-error">{error}</div>}
      <table className="data-table tax-map-table">
        <thead>
          <tr>
            <th>Set &amp; Ledger tax code</th>
            <th style={{ textAlign: "right" }}>Receipts</th>
            <th>QuickBooks tax code</th>
          </tr>
        </thead>
        <tbody>
          {data.codes.map((c) => (
            <tr key={c.key} className={c.source === "none" && c.receipt_count > 0 ? "unmapped" : ""}>
              <td>{c.label}</td>
              <td className="num">
                {c.receipt_count}
                {c.pending_count > 0 && <span className="muted" style={{ display: "block", fontSize: 12 }}>
                  {c.pending_count} to sync</span>}
              </td>
              <td>
                <div className="tax-map-pick">
                  <select
                    value={c.source === "mapped" ? c.qbo_tax_code_id ?? "" : ""}
                    disabled={busy !== null}
                    onChange={(e) => run(c.key, () => quickbooks.setTaxMapping(c.key, e.target.value || null))}
                    aria-label={`QuickBooks code for ${c.label}`}
                  >
                    <option value="">
                      {c.source === "none"
                        ? "No matching code"
                        : `Automatic: ${c.qbo_tax_code_name ?? "matching code"}`}
                    </option>
                    {/* Untaxed codes map to 0% codes, taxed ones to codes that charge tax. */}
                    {data.qbo_codes
                      .filter((q) => q.id === (c.source === "mapped" ? c.qbo_tax_code_id : null)
                        || ((q.purchase_rate || 0) > 0 || (q.sales_rate || 0) > 0) === c.rate > 0)
                      .map((q) => (
                        <option key={q.id} value={q.id}>{q.name}{rateLabel(q.purchase_rate ?? q.sales_rate)}</option>
                      ))}
                  </select>
                  {c.creatable && (
                    <button className="btn btn-primary btn-sm" disabled={busy !== null} onClick={() => create(c.key, c.label)}>
                      {busy === c.key ? "Creating…" : "Create in QuickBooks"}
                    </button>
                  )}
                </div>
                {c.source === "none" && c.receipt_count > 0 && (
                  <span className="muted" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                    Until it's mapped, the credit is claimed under the nearest larger code on part of the amount.
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.receipts.length > 0 && (
        <>
          <h4 className="tax-map-sub">Receipts waiting to sync with a note</h4>
          <table className="data-table tax-check-table">
            <thead>
              <tr>
                <th>Receipt</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Tax</th>
                <th>What happens in QuickBooks</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.receipt_id}>
                  <td>
                    <a href={`/app/receipts/${r.receipt_id}`}>{r.vendor_name || "Receipt"}</a>
                    <span className="muted" style={{ display: "block", fontSize: 12 }}>{r.invoice_date}</span>
                  </td>
                  <td className="num">{formatMoney(r.total_cents, r.currency)}</td>
                  <td className="num">{formatMoney(r.tax_cents, r.currency)}</td>
                  <td>
                    {r.note}
                    <span className="muted" style={{ display: "block", fontSize: 12 }}>
                      {r.lines.map((l) => `${formatMoney(l.amount_cents, r.currency)} · ${l.tax_code ?? "no code"}`).join("  +  ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.receipts.length > 6 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer" : `Show all ${data.receipts.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
